import {
  FC,
  useEffect,
  useState,
  createContext,
  ReactNode,
  useContext,
  Dispatch,
} from 'react'
import { User, onAuthStateChanged, sendEmailVerification } from 'firebase/auth'
import CircularProgress from '@mui/material/CircularProgress'
import 'twin.macro'
import { auth } from '@/firebase'
import { getUserInfo } from '@/firebase/user/actions'
import { DocumentData } from '@firebase/firestore'
import { SocialType, UserType } from '@/types/user'
import {
  getMySocialProfile,
  listenSocialProfile,
} from '@/firebase/social/actions'

type AuthStateProps = {
  currentUser: User | null | undefined
  isCheckedAuth: boolean
  userInfo: UserType
  social: SocialType
}

const AuthContext = createContext<AuthStateProps>({
  currentUser: undefined,
  isCheckedAuth: false,
  userInfo: null,
  social: null,
})

export const useAuthState = () => useContext(AuthContext)

const AuthState: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null | undefined>(
    undefined,
  )
  const [isCheckedAuth, setIsCheckedAuth] = useState<boolean>(false)
  const [emaiSended, setEmailSended] = useState(false)
  const [userInfo, setUserInfo] = useState<
    | DocumentData
    | {
        email: string
        name: string
        birthday: string
        createdAt: Date
        gender: 'M' | 'F'
        id: string
      }
    | null
    | undefined
  >(null)
  const [social, setSocial] = useState<
    DocumentData | SocialType | null | undefined
  >(null)

  useEffect(() => {
    let unSub = () => {}
    const cleanup = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user)
        setIsCheckedAuth(true)
        const userInfo = await getUserInfo()
        setUserInfo(userInfo)
        unSub = listenSocialProfile(user.uid, setSocial)
      } else {
        setIsCheckedAuth(true)
        setCurrentUser(undefined)
        setUserInfo(null)
        setSocial(null)
      }
    })
    return () => {
      cleanup()
      unSub()
    }
  }, [])

  if (isCheckedAuth) {
    return (
      <AuthContext.Provider
        value={{ currentUser, isCheckedAuth, userInfo, social }}
      >
        {!currentUser?.emailVerified && currentUser && (
          <p
            tw="fixed top-20 z-10 bg-red-400 text-white w-full text-center py-2 underline"
            onClick={async () => {
              try {
                await sendEmailVerification(auth.currentUser)
                setEmailSended(true)
              } catch (error) {
                console.log(error)
              }
            }}
          >
            {emaiSended
              ? '認証メールを送信しました'
              : 'メールアドレスを認証してください'}
          </p>
        )}
        {children}
      </AuthContext.Provider>
    )
  } else {
    return (
      <div tw="flex justify-center items-center w-screen h-screen">
        <CircularProgress size={120} />
      </div>
    )
  }
}

export default AuthState
